// src/components/Entity/ExerciceDetailModal.js
// Modal affichant le détail complet d'un exercice avec toutes les charges obligatoires
import React, { useState, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { calculateCorporateTax } from '../../utils/corporateTaxCalculations';
import './ExerciceDetailModal.css';

// Définition des charges obligatoires avec seuils et calculs
const CHARGES_OBLIGATOIRES = {
  // CET (Contribution Économique Territoriale)
  cfe: {
    key: 'cfe',
    label: 'CFE',
    fullLabel: 'Cotisation Foncière des Entreprises',
    help: 'Taxe locale due par toutes les entreprises. Exonérée la 1ère année, -50% la 2ème année.',
    category: 'fiscale',
    calculate: (ctx) => {
      // CFE = max(base imposable × taux, minimum selon CA)
      const cfeCalculee = ctx.cfeEstimate || 0;
      // Cotisation minimum CFE selon CA (barème 2024)
      let cfeMin = 0;
      const ca = ctx.ca || 0;
      if (ca > 500000) cfeMin = 535;
      else if (ca > 250000) cfeMin = 380;
      else if (ca > 100000) cfeMin = 237;
      else cfeMin = 237; // Minimum par défaut

      const cfeAvantExo = Math.max(cfeCalculee, cfeMin);

      if (ctx.exerciceNumber === 1) return { value: 0, note: 'Exonéré année 1' };
      if (ctx.exerciceNumber === 2) return { value: Math.round(cfeAvantExo * 0.5), note: '-50% année 2' };
      return { value: cfeAvantExo, note: `max(${cfeCalculee}€, min ${cfeMin}€)` };
    }
  },
  cvae: {
    key: 'cvae',
    label: 'CVAE',
    fullLabel: 'Cotisation sur la Valeur Ajoutée',
    help: 'Due si CA > 500 000€. Taux progressif de 0,28% à 0,75% de la valeur ajoutée.',
    category: 'fiscale',
    calculate: (ctx) => {
      if (ctx.ca < 500000) return { value: 0, note: 'CA < 500k€ → non due' };
      // Calcul simplifié : VA ≈ 30% du CA, taux moyen 0,5%
      const va = ctx.ca * 0.30;
      const cvae = Math.round(va * 0.005);
      return { value: cvae, note: `VA estimée: ${Math.round(va).toLocaleString('fr-FR')}€` };
    }
  },
  // Charges sur masse salariale
  taxeApprentissage: {
    key: 'taxeApprentissage',
    label: 'Taxe apprentissage',
    fullLabel: "Taxe d'apprentissage",
    help: '0,68% de la masse salariale brute (0,44% Alsace-Moselle). Finance la formation.',
    category: 'salariale',
    calculate: (ctx) => {
      if (!ctx.hasEmployees && ctx.masseSalariale === 0) return { value: 0, note: 'Pas de salariés' };
      const taxe = Math.round(ctx.masseSalariale * 0.0068);
      return { value: taxe, note: '0,68% masse salariale' };
    }
  },
  formationPro: {
    key: 'formationPro',
    label: 'Formation pro',
    fullLabel: 'Contribution Formation Professionnelle',
    help: '0,55% si <11 salariés, 1% si ≥11 salariés. Finance le CPF et la formation.',
    category: 'salariale',
    calculate: (ctx) => {
      if (!ctx.hasEmployees && ctx.masseSalariale === 0) return { value: 0, note: 'Pas de salariés' };
      const taux = ctx.effectif >= 11 ? 0.01 : 0.0055;
      const contrib = Math.round(ctx.masseSalariale * taux);
      return { value: contrib, note: `${taux * 100}% masse salariale` };
    }
  },
  versementMobilite: {
    key: 'versementMobilite',
    label: 'Versement mobilité',
    fullLabel: 'Versement mobilité (ex-transport)',
    help: 'Due si >10 salariés dans une zone couverte. Taux variable selon commune (1% à 3%).',
    category: 'salariale',
    calculate: (ctx) => {
      if (ctx.effectif <= 10) return { value: 0, note: '≤10 salariés → non due' };
      // Taux moyen estimé 2%
      const vm = Math.round(ctx.masseSalariale * 0.02);
      return { value: vm, note: '~2% masse salariale (variable)' };
    }
  },
  peec: {
    key: 'peec',
    label: 'PEEC',
    fullLabel: 'Participation Effort Construction',
    help: 'Due si ≥50 salariés. 0,45% de la masse salariale pour Action Logement.',
    category: 'salariale',
    calculate: (ctx) => {
      if (ctx.effectif < 50) return { value: 0, note: '<50 salariés → non due' };
      const peec = Math.round(ctx.masseSalariale * 0.0045);
      return { value: peec, note: '0,45% masse salariale' };
    }
  },
  agefiph: {
    key: 'agefiph',
    label: 'AGEFIPH',
    fullLabel: 'Contribution handicap',
    help: 'Due si ≥20 salariés et quota 6% travailleurs handicapés non atteint.',
    category: 'salariale',
    calculate: (ctx) => {
      if (ctx.effectif < 20) return { value: 0, note: '<20 salariés → non due' };
      // Estimation : 1 unité manquante = 400€/mois
      return { value: 0, note: 'Estimé si quota non atteint' };
    }
  }
};

const ExerciceDetailModal = ({
  year,
  ca,
  // Toutes les hypothèses du step 2
  managers = [],
  hasEmployees = false,
  employeesMode = 'simplified',
  employeesSimplified = {},
  employeeProfiles = [],
  chargeMode = 'simplified',
  simplifiedCharges = {},
  exploitationLines = {},
  // Options
  hasACRE = false,
  hasCGA = false,
  advancedOptions = {},
  cfeEstimate = 500,
  // Contexte exercice
  exerciceNumber = 1,
  taxRegime = 'IS',
  entityInfo = {},
  // Overrides
  initialOverrides = {},
  onSave,
  onClose
}) => {
  // State pour les overrides manuels de TOUTES les lignes
  const [overrides, setOverrides] = useState({
    ca: initialOverrides.ca ?? null,
    // Charges exploitation
    chargesExploitation: initialOverrides.chargesExploitation ?? null,
    // Charges obligatoires
    cfe: initialOverrides.cfe ?? null,
    cvae: initialOverrides.cvae ?? null,
    taxeApprentissage: initialOverrides.taxeApprentissage ?? null,
    formationPro: initialOverrides.formationPro ?? null,
    versementMobilite: initialOverrides.versementMobilite ?? null,
    peec: initialOverrides.peec ?? null,
    agefiph: initialOverrides.agefiph ?? null,
    // Dirigeants (par index)
    ...initialOverrides
  });

  // State pour afficher les formules
  const [showFormulas, setShowFormulas] = useState(true);

  const isFirstYear = exerciceNumber === 1;
  const isSecondYear = exerciceNumber === 2;

  // ==================== CALCULS DE BASE ====================

  const caEffectif = overrides.ca !== null ? overrides.ca : ca;

  // Masse salariale et effectif
  const { masseSalariale, effectif } = useMemo(() => {
    if (!hasEmployees) return { masseSalariale: 0, effectif: 0 };

    if (chargeMode === 'simplified' || employeesMode === 'simplified') {
      const { count = 0, avgGrossSalary = 0, months = 12 } = employeesSimplified;
      return {
        masseSalariale: count * avgGrossSalary * months,
        effectif: count
      };
    } else {
      let total = 0;
      let eff = 0;
      employeeProfiles.forEach(p => {
        total += p.grossMonthlySalary * 12 * p.headcount;
        eff += p.headcount;
      });
      return { masseSalariale: total, effectif: eff };
    }
  }, [hasEmployees, chargeMode, employeesMode, employeesSimplified, employeeProfiles]);

  // Contexte pour les calculs de charges obligatoires
  const calculContext = useMemo(() => ({
    ca: caEffectif,
    exerciceNumber,
    cfeEstimate,
    hasEmployees,
    masseSalariale,
    effectif,
    hasACRE,
    advancedOptions
  }), [caEffectif, exerciceNumber, cfeEstimate, hasEmployees, masseSalariale, effectif, hasACRE, advancedOptions]);

  // Calcul des charges obligatoires
  const chargesObligatoires = useMemo(() => {
    const result = {};
    Object.entries(CHARGES_OBLIGATOIRES).forEach(([key, def]) => {
      const calculated = def.calculate(calculContext);
      result[key] = {
        ...def,
        calculated: calculated.value,
        note: calculated.note,
        effective: overrides[key] !== null ? overrides[key] : calculated.value,
        isOverridden: overrides[key] !== null
      };
    });
    return result;
  }, [calculContext, overrides]);

  // Total charges obligatoires
  const totalChargesObligatoires = useMemo(() => {
    return Object.values(chargesObligatoires).reduce((sum, c) => sum + c.effective, 0);
  }, [chargesObligatoires]);

  // Charges d'exploitation
  const chargesExploitationCalculees = useMemo(() => {
    if (chargeMode === 'simplified') {
      if (simplifiedCharges.usePercent) {
        return Math.round(caEffectif * (simplifiedCharges.totalPercent || 0) / 100);
      }
      return simplifiedCharges.totalFixed || 0;
    } else {
      let total = 0;
      Object.values(exploitationLines).forEach(line => {
        if (line.enabled) {
          if (line.type === 'percent') {
            total += Math.round(caEffectif * (line.value || 0) / 100);
          } else {
            total += line.value || 0;
          }
        }
      });
      return total;
    }
  }, [chargeMode, simplifiedCharges, exploitationLines, caEffectif]);

  const chargesExploitation = overrides.chargesExploitation !== null
    ? overrides.chargesExploitation
    : chargesExploitationCalculees;

  // Coût dirigeants avec ACRE/JEI
  const coutDirigeantsDetail = useMemo(() => {
    return managers.map((m, idx) => {
      let charges = m.charges || 0;
      let cout = m.cout || 0;

      // ACRE année 1
      if (hasACRE && isFirstYear) {
        charges = Math.round(charges * 0.5);
        cout = (m.net || 0) + charges;
      }

      // JEI
      if (advancedOptions.jei && exerciceNumber <= 7) {
        const jeiReduction = exerciceNumber <= 3 ? 0.5 : (exerciceNumber <= 5 ? 0.3 : 0.2);
        charges = Math.round(charges * (1 - jeiReduction));
        cout = (m.net || 0) + charges;
      }

      // Override possible
      const overrideKey = `manager_${idx}`;
      if (overrides[overrideKey] !== undefined && overrides[overrideKey] !== null) {
        cout = overrides[overrideKey];
      }

      return {
        ...m,
        index: idx,
        chargesAjustees: charges,
        coutAjuste: cout,
        isOverridden: overrides[`manager_${idx}`] !== null && overrides[`manager_${idx}`] !== undefined,
        acreApplied: hasACRE && isFirstYear,
        jeiApplied: advancedOptions.jei && exerciceNumber <= 7
      };
    });
  }, [managers, hasACRE, isFirstYear, advancedOptions.jei, exerciceNumber, overrides]);

  const coutDirigeantsTotal = coutDirigeantsDetail.reduce((sum, m) => sum + (m.coutAjuste || 0), 0);

  // Coût employés
  const coutEmployesCalcule = useMemo(() => {
    if (!hasEmployees) return 0;
    const chargeRate = 1.45;
    let cost = masseSalariale * chargeRate;

    if (advancedOptions.jei && exerciceNumber <= 7) {
      const jeiReduction = exerciceNumber <= 3 ? 0.5 : (exerciceNumber <= 5 ? 0.3 : 0.2);
      cost = cost * (1 - jeiReduction * 0.45);
    }

    return Math.round(cost);
  }, [hasEmployees, masseSalariale, advancedOptions.jei, exerciceNumber]);

  const coutEmployes = overrides.coutEmployes !== null ? overrides.coutEmployes : coutEmployesCalcule;

  // Total des charges
  const totalCharges = chargesExploitation + coutDirigeantsTotal + coutEmployes + totalChargesObligatoires;

  // Résultat avant impôt
  const resultatBrut = caEffectif - totalCharges;

  // IS / IR
  const fiscalResult = useMemo(() => {
    if (taxRegime !== 'IS' || resultatBrut <= 0) {
      return { is: 0, resultatNet: resultatBrut, isDetails: {} };
    }

    let baseImposable = resultatBrut;

    if (advancedOptions.jei) {
      if (exerciceNumber <= 3) {
        return { is: 0, resultatNet: resultatBrut, isDetails: { jeiExoneration: true } };
      } else if (exerciceNumber <= 5) {
        baseImposable = resultatBrut * 0.5;
      }
    }

    const isResult = calculateCorporateTax(baseImposable, year, true);

    let cirCredit = 0;
    if (advancedOptions.cir && advancedOptions.cirAmount > 0) {
      cirCredit = Math.round(advancedOptions.cirAmount * 0.30);
    }

    const isFinal = Math.max(0, isResult.is - cirCredit);

    return {
      is: isFinal,
      isBeforeCIR: isResult.is,
      cirCredit,
      resultatNet: resultatBrut - isFinal,
      isDetails: isResult.details
    };
  }, [taxRegime, resultatBrut, advancedOptions, exerciceNumber, year]);

  // ==================== HANDLERS ====================

  const handleOverride = (key, value) => {
    setOverrides(prev => ({
      ...prev,
      [key]: value === '' ? null : parseFloat(value)
    }));
  };

  const resetOverride = (key) => {
    setOverrides(prev => ({ ...prev, [key]: null }));
  };

  const handleSave = () => {
    onSave(overrides);
    onClose();
  };

  // ==================== HELPERS ====================

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const getRoleLabel = (role) => {
    const labels = {
      'president': 'Président',
      'dg': 'Directeur Général',
      'gerant': 'Gérant',
      'co-gerant': 'Co-gérant',
    };
    return labels[role] || role;
  };

  // Aide contextuelle pour chaque type de charge d'exploitation
  const getExploitationLineHelp = (key) => {
    const helps = {
      'loyer': 'Loyer des locaux professionnels (bureaux, entrepôts, commerces)',
      'comptable': 'Honoraires d\'expert-comptable et frais de tenue comptable',
      'assurances': 'Assurances professionnelles (RC Pro, locaux, véhicules)',
      'telecommunications': 'Téléphone, internet, abonnements télécom',
      'fournitures': 'Fournitures de bureau, consommables',
      'entretien': 'Entretien et réparations des locaux et équipements',
      'deplacement': 'Frais de déplacement, transport, carburant',
      'publicite': 'Publicité, communication, marketing',
      'soustraitance': 'Sous-traitance et prestations externes',
      'services_bancaires': 'Frais bancaires, commissions, intérêts',
      'formation': 'Formation professionnelle (hors contribution obligatoire)',
      'autres': 'Autres charges d\'exploitation diverses'
    };
    return helps[key] || 'Charge d\'exploitation';
  };

  // State pour le tooltip actif
  const [activeTooltip, setActiveTooltip] = useState(null);

  // ==================== RENDER ====================

  // Composant Tooltip inline
  const Tooltip = ({ id, text, children }) => (
    <span className="tooltip-wrapper">
      {children}
      <span
        className="help-icon"
        onMouseEnter={() => setActiveTooltip(id)}
        onMouseLeave={() => setActiveTooltip(null)}
        onClick={() => setActiveTooltip(activeTooltip === id ? null : id)}
      >
        ?
      </span>
      {activeTooltip === id && (
        <span className="tooltip-content">{text}</span>
      )}
    </span>
  );

  const renderEditableRow = (key, label, calculatedValue, help, note, isOverridden) => {
    const effectiveValue = overrides[key] !== null ? overrides[key] : calculatedValue;
    return (
      <div key={key} className={`detail-row editable ${isOverridden ? 'overridden' : ''}`}>
        <div className="row-label-with-help">
          <span className="row-label">{label}</span>
          {help && (
            <Tooltip id={key} text={help}>
              <span></span>
            </Tooltip>
          )}
        </div>
        <div className="row-value-edit">
          {showFormulas && note && !isOverridden && (
            <span className="formula-hint">{note}</span>
          )}
          <div className="edit-value">
            <input
              type="number"
              value={effectiveValue}
              onChange={(e) => handleOverride(key, e.target.value)}
              className={isOverridden ? 'overridden' : ''}
            />
            <span className="suffix">€</span>
            {isOverridden && (
              <button className="reset-btn" onClick={() => resetOverride(key)} title="Revenir au calcul">↺</button>
            )}
          </div>
        </div>
      </div>
    );
  };

  const modalContent = (
    <div className="exercice-detail-modal-overlay" onClick={onClose}>
      <div className="exercice-detail-modal" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="modal-header">
          <div className="header-info">
            <div className="entity-badge-row">
              <span className="legal-form-badge">{entityInfo.legalForm}</span>
              <span className="entity-name">{entityInfo.name}</span>
              <span className={`tax-regime-badge ${taxRegime?.toLowerCase()}`}>{taxRegime}</span>
              {isFirstYear && <span className="year-badge first">Année 1</span>}
              {isSecondYear && <span className="year-badge second">Année 2</span>}
            </div>
            <h3>Exercice {year}</h3>
          </div>
          <div className="header-actions">
            <label className="toggle-formulas">
              <input
                type="checkbox"
                checked={showFormulas}
                onChange={(e) => setShowFormulas(e.target.checked)}
              />
              <span>Afficher calculs</span>
            </label>
            <button className="close-btn" onClick={onClose}>×</button>
          </div>
        </div>

        {/* Body */}
        <div className="modal-body">
          {/* Options actives */}
          <div className="active-options-bar">
            {hasACRE && <span className="option-tag acre">ACRE</span>}
            {advancedOptions.jei && <span className="option-tag jei">JEI</span>}
            {advancedOptions.cir && <span className="option-tag cir">CIR</span>}
            {advancedOptions.regimeMereFille && <span className="option-tag mf">Mère-Fille</span>}
            {hasCGA && <span className="option-tag cga">CGA</span>}
            {!hasACRE && !advancedOptions.jei && !advancedOptions.cir && (
              <span className="no-options">Aucune option fiscale active</span>
            )}
          </div>

          {/* Colonnes */}
          <div className="modal-columns">
            {/* Colonne gauche : Produits et charges */}
            <div className="modal-column left">
              {/* Section CA */}
              <div className="detail-section ca-section">
                <h4>📊 Chiffre d'affaires</h4>
                {renderEditableRow(
                  'ca',
                  'CA de l\'exercice',
                  ca,
                  'Chiffre d\'affaires total de l\'exercice',
                  null,
                  overrides.ca !== null
                )}
              </div>

              {/* Section Dirigeants */}
              {managers.length > 0 && (
                <div className="detail-section managers-section">
                  <h4>👤 Coût dirigeants</h4>
                  {coutDirigeantsDetail.map((m, idx) => (
                    <div key={idx} className={`manager-detail-row ${m.isOverridden ? 'overridden' : ''}`}>
                      <div className="manager-info">
                        <span className="manager-role">{getRoleLabel(m.role)}</span>
                        <span className={`status-badge ${m.status === 'TNS' ? 'tns' : 'as'}`}>
                          {m.status === 'TNS' ? 'TNS' : 'AS'}
                        </span>
                        {m.acreApplied && <span className="tag-small acre">ACRE</span>}
                        {m.jeiApplied && <span className="tag-small jei">JEI</span>}
                      </div>
                      <div className="manager-edit">
                        {showFormulas && !m.isOverridden && (
                          <span className="formula-hint">
                            {formatCurrency(m.net)} net + {formatCurrency(m.chargesAjustees)} charges
                          </span>
                        )}
                        <div className="edit-value compact">
                          <input
                            type="number"
                            value={m.coutAjuste}
                            onChange={(e) => handleOverride(`manager_${idx}`, e.target.value)}
                            className={m.isOverridden ? 'overridden' : ''}
                          />
                          <span className="suffix">€</span>
                          {m.isOverridden && (
                            <button className="reset-btn" onClick={() => resetOverride(`manager_${idx}`)}>↺</button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                  <div className="section-total">
                    <span>Total dirigeants</span>
                    <span className="negative">{formatCurrency(coutDirigeantsTotal)}</span>
                  </div>
                </div>
              )}

              {/* Section Employés */}
              {hasEmployees && (
                <div className="detail-section employees-section">
                  <h4>👥 Coût personnel</h4>
                  {renderEditableRow(
                    'coutEmployes',
                    `${effectif} employé(s)`,
                    coutEmployesCalcule,
                    'Masse salariale brute + charges patronales (~45%)',
                    `${formatCurrency(masseSalariale)} × 1.45`,
                    overrides.coutEmployes !== null
                  )}
                </div>
              )}

              {/* Section Charges exploitation */}
              <div className="detail-section exploitation-section">
                <h4>🏢 Charges d'exploitation</h4>

                {chargeMode === 'simplified' ? (
                  // Mode simplifié : une seule ligne
                  renderEditableRow(
                    'chargesExploitation',
                    simplifiedCharges.usePercent ? 'Forfait % du CA' : 'Forfait fixe annuel',
                    chargesExploitationCalculees,
                    'Total des charges d\'exploitation (loyers, assurances, fournitures, etc.)',
                    simplifiedCharges.usePercent
                      ? `${simplifiedCharges.totalPercent || 0}% × ${formatCurrency(caEffectif)}`
                      : 'Montant fixe annuel',
                    overrides.chargesExploitation !== null
                  )
                ) : (
                  // Mode détaillé : afficher chaque ligne
                  <>
                    {Object.entries(exploitationLines)
                      .filter(([_, line]) => line.enabled)
                      .map(([key, line]) => {
                        const lineLabel = key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' ');
                        const lineValue = line.type === 'percent'
                          ? Math.round(caEffectif * (line.value || 0) / 100)
                          : (line.value || 0);
                        const lineNote = line.type === 'percent'
                          ? `${line.value}% du CA`
                          : 'Montant fixe';
                        const lineHelp = getExploitationLineHelp(key);

                        return renderEditableRow(
                          `exploitation_${key}`,
                          lineLabel,
                          lineValue,
                          lineHelp,
                          lineNote,
                          overrides[`exploitation_${key}`] !== null
                        );
                      })}
                    {Object.keys(exploitationLines).filter(k => exploitationLines[k].enabled).length === 0 && (
                      <div className="no-charges-hint">Aucune charge détaillée définie</div>
                    )}
                    <div className="section-total">
                      <span>Total exploitation</span>
                      <span className="negative">{formatCurrency(chargesExploitation)}</span>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Colonne droite : Charges obligatoires */}
            <div className="modal-column right">
              <div className="detail-section charges-obligatoires-section">
                <h4>🏛️ Charges obligatoires</h4>
                <p className="section-subtitle">Impôts et taxes obligatoires</p>

                {/* Charges fiscales */}
                <div className="charges-group">
                  <span className="group-label">Contribution Économique Territoriale</span>
                  {Object.values(chargesObligatoires)
                    .filter(c => c.category === 'fiscale')
                    .map(charge => renderEditableRow(
                      charge.key,
                      charge.label,
                      charge.calculated,
                      charge.help,
                      charge.note,
                      charge.isOverridden
                    ))}
                </div>

                {/* Charges salariales */}
                <div className="charges-group">
                  <span className="group-label">Charges sur masse salariale</span>
                  {Object.values(chargesObligatoires)
                    .filter(c => c.category === 'salariale')
                    .map(charge => renderEditableRow(
                      charge.key,
                      charge.label,
                      charge.calculated,
                      charge.help,
                      charge.note,
                      charge.isOverridden
                    ))}
                </div>

                <div className="section-total">
                  <span>Total charges obligatoires</span>
                  <span className="negative">{formatCurrency(totalChargesObligatoires)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Section Résumé */}
          <div className="summary-section">
            <h4>📈 Compte de résultat simplifié</h4>

            <div className="summary-grid">
              <div className="summary-row ca">
                <span className="row-label">Chiffre d'affaires</span>
                <span className="row-value">{formatCurrency(caEffectif)}</span>
              </div>

              <div className="summary-row charges">
                <span className="row-label">− Coût dirigeants</span>
                <span className="row-value negative">{formatCurrency(coutDirigeantsTotal)}</span>
              </div>

              {hasEmployees && (
                <div className="summary-row charges">
                  <span className="row-label">− Coût personnel</span>
                  <span className="row-value negative">{formatCurrency(coutEmployes)}</span>
                </div>
              )}

              <div className="summary-row charges">
                <span className="row-label">− Charges exploitation</span>
                <span className="row-value negative">{formatCurrency(chargesExploitation)}</span>
              </div>

              <div className="summary-row charges">
                <span className="row-label">− Charges obligatoires</span>
                <span className="row-value negative">{formatCurrency(totalChargesObligatoires)}</span>
              </div>

              <div className="summary-row resultat">
                <span className="row-label">= Résultat avant impôt</span>
                <span className={`row-value ${resultatBrut < 0 ? 'negative' : ''}`}>
                  {formatCurrency(resultatBrut)}
                </span>
              </div>

              {taxRegime === 'IS' && resultatBrut > 0 && (
                <>
                  {fiscalResult.isDetails?.jeiExoneration ? (
                    <div className="summary-row is">
                      <span className="row-label">
                        − IS <span className="is-detail">(exonéré JEI)</span>
                      </span>
                      <span className="row-value">0 €</span>
                    </div>
                  ) : (
                    <>
                      <div className="summary-row is">
                        <span className="row-label">
                          − IS
                          {fiscalResult.cirCredit > 0 && (
                            <span className="is-detail">
                              (avant CIR: {formatCurrency(fiscalResult.isBeforeCIR)})
                            </span>
                          )}
                        </span>
                        <span className="row-value negative">{formatCurrency(fiscalResult.is)}</span>
                      </div>
                      {fiscalResult.cirCredit > 0 && (
                        <div className="summary-row cir-credit">
                          <span className="row-label">dont CIR déduit</span>
                          <span className="row-value positive">+{formatCurrency(fiscalResult.cirCredit)}</span>
                        </div>
                      )}
                    </>
                  )}

                  <div className="summary-row net highlight">
                    <span className="row-label">= Résultat net</span>
                    <span className="row-value">{formatCurrency(fiscalResult.resultatNet)}</span>
                  </div>
                </>
              )}

              {taxRegime === 'IR' && (
                <div className="ir-notice">
                  <span>Société IR : le résultat sera imposé chez les associés</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="modal-footer">
          <div className="footer-info">
            {Object.values(overrides).some(v => v !== null) && (
              <span className="override-count">
                {Object.values(overrides).filter(v => v !== null).length} valeur(s) modifiée(s)
              </span>
            )}
          </div>
          <div className="footer-actions">
            <button className="btn-cancel" onClick={onClose}>Fermer</button>
            <button className="btn-save" onClick={handleSave}>Enregistrer</button>
          </div>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};

export default ExerciceDetailModal;
